import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useStore } from 'react-redux';

import { selectAccessToken, selectCurrentLanguage } from '@shared/api/reduxToolkit';
import { useGetCurrentUserQuery } from '@shared/api/rtkQuery';
import { fromPlatformLanguage, toPlatformLanguage } from '@shared/config/i18n';
import { useActions } from '@shared/lib/hooks';

// Shows the portal in the language stored on the account (the platform's source of truth),
// whenever that language loads or changes — e.g. after login, or after it was changed in the
// app. A portal-only language that maps to the stored one is kept: French in the portal with
// en on the account stays French.
//
// Only the account language triggers this. A pick in the language switcher patches the
// cached account language first, so it is never switched back here.
export const useAccountLanguage = ({ isRehydrated }) => {
	const store = useStore();
	const { i18n } = useTranslation();
	const { setLanguage } = useActions();
	const accessToken = useSelector(selectAccessToken);
	// Subscribes rather than only reading the cache: after a reload AppInitializers skips
	// /v1/user (accountMembershipId is persisted), so without this the account language would
	// only arrive on pages that fetch the user themselves. Deduped with those subscriptions.
	const { data: accountLanguage } = useGetCurrentUserQuery(undefined, {
		skip: !accessToken,
		selectFromResult: ({ data }) => ({ data: data?.language }),
	});

	useEffect(() => {
		if (!isRehydrated || !accountLanguage) return;

		const currentLanguage = selectCurrentLanguage(store.getState());
		if (currentLanguage && toPlatformLanguage(currentLanguage) === toPlatformLanguage(accountLanguage)) return;

		const lang = fromPlatformLanguage(accountLanguage);
		setLanguage(lang);
		i18n.changeLanguage(lang);
	}, [accountLanguage, i18n, isRehydrated, setLanguage, store]);
};
